/**
 * Delivery Artifacts Hook
 * Manages Delivery workspace artifacts (Technical Architecture, Story Map, MVP Scope) with version history
 */

import { useQuery } from '@tanstack/react-query';
import { useAuthToken } from './use-auth';

// Types
export type DeliveryType = 'architecture' | 'storymap' | 'mvp' | 'stories';

export interface DeliveryArtifactVersion {
  id: string;
  version: number;
  content: Record<string, unknown>;
  createdAt: string;
  creator: {
    id: string;
    name: string | null;
    email: string;
  };
}

export interface DeliveryVersion {
  id: string;
  name: string;
  timestamp?: string;
  isCurrent?: boolean;
}

// API Functions
async function fetchDeliveryVersions(
  projectId: string,
  type: DeliveryType,
  token: string
): Promise<DeliveryArtifactVersion[]> {
  const response = await fetch(`/api/projects/${projectId}/delivery/${type}/versions`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      // No artifact yet, return empty array
      return [];
    }
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || 'Failed to fetch delivery versions');
  }

  const data = await response.json();
  return data.data || [];
}

// Transform API response to Version format for the dropdown
function transformToDropdownVersions(versions: DeliveryArtifactVersion[]): DeliveryVersion[] {
  if (!versions || versions.length === 0) {
    return [{ id: 'initial', name: 'Version 1', isCurrent: true }];
  }

  return versions.map((v, index) => ({
    id: v.id,
    name: `Version ${v.version}`,
    timestamp: new Date(v.createdAt).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }),
    isCurrent: index === 0, // Most recent is current
  }));
}

// Helper to extract markdown from various content shapes
function extractMarkdown(content: unknown): string | null {
  if (!content) return null;
  if (typeof content === 'string' && content.trim()) return content;
  if (typeof content === 'object' && content !== null) {
    const c = content as Record<string, unknown>;
    const md = c.markdown ?? c.content;
    if (typeof md === 'string' && md.trim()) return md;
  }
  return null;
}

// Fetch single delivery artifact (for content persistence)
async function fetchDeliveryArtifact(
  projectId: string,
  type: DeliveryType,
  token: string
): Promise<{ content?: unknown } | null> {
  const response = await fetch(`/api/projects/${projectId}/delivery/${type}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || 'Failed to fetch delivery artifact');
  }

  const data = await response.json();
  return data.data ?? null;
}

// Fetch all delivery artifacts (for sidebar indicators)
async function fetchDeliveryArtifactsList(
  projectId: string,
  token: string
): Promise<Array<{ type: string; content?: unknown }>> {
  const response = await fetch(`/api/projects/${projectId}/delivery`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    if (response.status === 404) return [];
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error?.message || 'Failed to fetch delivery artifacts');
  }
  const data = await response.json();
  return data.data ?? [];
}

export function useDeliveryArtifact(projectId: string | undefined, type: DeliveryType) {
  const token = useAuthToken();

  return useQuery({
    queryKey: ['delivery', projectId, type, 'artifact'],
    queryFn: async () => {
      if (!projectId || !token) return null;
      return fetchDeliveryArtifact(projectId, type, token);
    },
    enabled: !!projectId && !!token,
    staleTime: 30000,
  });
}

export function useDeliveryArtifactsList(projectId: string | undefined) {
  const token = useAuthToken();
  return useQuery({
    queryKey: ['delivery', projectId, 'list'],
    queryFn: async () => {
      if (!projectId || !token) return [];
      return fetchDeliveryArtifactsList(projectId, token);
    },
    enabled: !!projectId && !!token,
    staleTime: 30000,
  });
}

export { extractMarkdown as extractDeliveryMarkdown };

// Hooks
export function useDeliveryVersions(projectId: string | undefined, type: DeliveryType) {
  const token = useAuthToken();

  return useQuery({
    queryKey: ['delivery', projectId, type, 'versions'],
    queryFn: async () => {
      if (!projectId || !token) {
        return [];
      }
      const versions = await fetchDeliveryVersions(projectId, type, token);
      return transformToDropdownVersions(versions);
    },
    enabled: !!projectId && !!token,
    staleTime: 30000, // 30 seconds
  });
}

export function useDeliveryVersionsRaw(projectId: string | undefined, type: DeliveryType) {
  const token = useAuthToken();

  return useQuery({
    queryKey: ['delivery', projectId, type, 'versions', 'raw'],
    queryFn: async () => {
      if (!projectId || !token) {
        return [];
      }
      return fetchDeliveryVersions(projectId, type, token);
    },
    enabled: !!projectId && !!token,
    staleTime: 30000,
  });
}
